import { getJson } from "@/api/terminalApi";

export type WalletSort =
	| "ev"
	| "edge"
	| "edge_lcb"
	| "z"
	| "brier"
	| "clv"
	| "volume"
	| "positions"
	| "resolved"
	| "views";

export interface WalletRow {
	account: string;
	wallet: string | null;
	category: "insider" | "bot";
	subcategory: string;
	reason: string;
	also_sniper: boolean;
	n_positions: number;
	n_resolved: number;
	n_wins: number;
	win_rate: number | null;
	n_markets: number;
	volume_usd: number | null;
	entry_vwap: number | null; // dollar-weighted
	entry_mean: number | null; // per position
	pnl_usd: number | null;
	ev_per_usd: number | null;
	edge: number | null;
	edge_lcb: number | null;
	z_score: number | null;
	brier: number | null;
	clv: number | null;
	hedged_markets: number;
	sniper_fills: number;
	avg_trades_per_position: number | null;
	first_seen: number | null;
	last_seen: number | null;
	views: number | null; // Polymarket profile views, null until fetched
	pm_trades: number | null;
}

export interface Pagination {
	page: number;
	limit: number;
	total: number;
	totalPages: number;
	hasPrev: boolean;
	hasNext: boolean;
}

export interface WalletQueryParams {
	category?: "insider" | "bot";
	subcategory?: string;
	sort: WalletSort;
	dir?: "asc" | "desc";
	minResolved: number;
	page: number;
	limit: number;
}

export interface SubcategorySummary {
	category: "insider" | "bot";
	subcategory: string;
	wallets: number;
	volume_usd: number | null;
	ev_per_usd: number | null;
	brier: number | null;
}

export interface CalibrationBucket {
	bucket: number;
	price_from: number;
	price_to: number;
	n: number;
	avg_entry: number | null;
	win_rate: number | null;
	edge: number | null;
	brier: number | null;
}

export interface WalletPosition {
	token_id: string;
	condition_id: string | null;
	question: string | null;
	slug: string | null;
	outcome: string | null;
	volume_usd: number | null;
	entry: number | null;
	trades: number;
	resolved: boolean;
	won: boolean | null;
	pnl_usd: number | null;
	brier: number | null;
	edge: number | null;
	clv: number | null;
	last_price: number | null;
	hedged: boolean;
}

export const fetchWallets = (p: WalletQueryParams) =>
	getJson<{ data: WalletRow[]; pagination: Pagination }>("/wallets", {
		category: p.category,
		subcategory: p.subcategory,
		sort: p.sort,
		dir: p.dir,
		min_resolved: p.minResolved || undefined,
		page: p.page,
		limit: p.limit,
	});

export const fetchWalletSubcategories = () =>
	getJson<{ data: SubcategorySummary[] }>("/wallets/subcategories");

export const fetchCalibration = () =>
	getJson<{ data: CalibrationBucket[] }>("/wallets/calibration");

export const fetchWalletPositions = (id: string) =>
	getJson<{ data: WalletPosition[] }>(`/wallets/${encodeURIComponent(id)}`);
